import type {
	DropdownMenuProps,
	SRT_FilterOption,
	SRT_Header,
	SRT_InternalFilterOption,
	SRT_Localization,
	SRT_RowData,
	SRT_TableInstance,
} from '../../types'

import { Fragment, useMemo } from 'react'

import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuSeparator,
} from '@/shared/components/ui/dropdown-menu'

import { SRT_ActionMenuItem } from './SRT_ActionMenuItem'

/* eslint-disable @typescript-eslint/no-unused-vars */

export const srtFilterOptions = (
	localization: SRT_Localization
): SRT_InternalFilterOption[] => [
	{
		divider: false,
		label: localization.filterFuzzy,
		option: 'fuzzy',
		symbol: '≈',
	},
	{
		divider: false,
		label: localization.filterContains,
		option: 'contains',
		symbol: '*',
	},
	{
		divider: false,
		label: localization.filterStartsWith,
		option: 'startsWith',
		symbol: 'a',
	},
	{
		divider: true,
		label: localization.filterEndsWith,
		option: 'endsWith',
		symbol: 'z',
	},
	{
		divider: false,
		label: localization.filterEquals,
		option: 'equals',
		symbol: '=',
	},
	{
		divider: true,
		label: localization.filterNotEquals,
		option: 'notEquals',
		symbol: '≠',
	},
	{
		divider: false,
		label: localization.filterBetween,
		option: 'between',
		symbol: '⇿',
	},
	{
		divider: true,
		label: localization.filterBetweenInclusive,
		option: 'betweenInclusive',
		symbol: '⬌',
	},
	{
		divider: false,
		label: localization.filterGreaterThan,
		option: 'greaterThan',
		symbol: '>',
	},
	{
		divider: false,
		label: localization.filterGreaterThanOrEqualTo,
		option: 'greaterThanOrEqualTo',
		symbol: '≥',
	},
	{
		divider: false,
		label: localization.filterLessThan,
		option: 'lessThan',
		symbol: '<',
	},
	{
		divider: true,
		label: localization.filterLessThanOrEqualTo,
		option: 'lessThanOrEqualTo',
		symbol: '≤',
	},
	{
		divider: false,
		label: localization.filterEmpty,
		option: 'empty',
		symbol: '∅',
	},
	{
		divider: false,
		label: localization.filterNotEmpty,
		option: 'notEmpty',
		symbol: '!∅',
	},
]

const rangeModes = ['between', 'betweenInclusive', 'inNumberRange']
const emptyModes = ['empty', 'notEmpty']
const arrModes = ['arrIncludesSome', 'arrIncludesAll', 'arrIncludes']
const rangeVariants = ['range-slider', 'date-range', 'datetime-range', 'range']

export type SRT_FilterOptionMenuProps<TData extends SRT_RowData> = {
	anchorEl: HTMLElement | null
	header?: SRT_Header<TData>
	onSelect?: () => void
	setAnchorEl: (anchorEl: HTMLElement | null) => void
	setFilterValue?: (filterValue: any) => void
	table: SRT_TableInstance<TData>
} & Partial<DropdownMenuProps>

export const SRT_FilterOptionMenu = <TData extends SRT_RowData>({
	anchorEl,
	header,
	onSelect,
	setAnchorEl,
	setFilterValue,
	table,
	...rest
}: SRT_FilterOptionMenuProps<TData>) => {
	const {
		getState,
		options: {
			columnFilterModeOptions,
			globalFilterModeOptions,
			localization,
			renderColumnFilterModeMenuItems,
			renderGlobalFilterModeMenuItems,
		},
		setColumnFilterFns,
		setGlobalFilterFn,
	} = table
	const { density, globalFilterFn } = getState()
	const { column } = header ?? {}
	const { columnDef } = column ?? {}
	const currentFilterValue = column?.getFilterValue()

	let allowedColumnFilterOptions =
		columnDef?.columnFilterModeOptions ?? columnFilterModeOptions

	if (rangeVariants.includes(columnDef?.filterVariant as string)) {
		allowedColumnFilterOptions = [
			...rangeModes,
			...(allowedColumnFilterOptions ?? []),
		].filter((option) => rangeModes.includes(option))
	}

	const internalFilterOptions = useMemo(
		() =>
			srtFilterOptions(localization).filter((filterOption) =>
				columnDef
					? allowedColumnFilterOptions === undefined ||
						allowedColumnFilterOptions?.includes(filterOption.option)
					: (!globalFilterModeOptions ||
							globalFilterModeOptions.includes(filterOption.option)) &&
						['contains', 'fuzzy', 'startsWith'].includes(filterOption.option)
			),
		[]
	)

	const handleSelectFilterMode = (option: SRT_FilterOption) => {
		const prevFilterMode = columnDef?._filterFn ?? ''
		if (!header || !column) {
			// global filter mode
			setGlobalFilterFn(option)
		} else if (option !== prevFilterMode) {
			// column filter mode
			setColumnFilterFns((prev: Record<string, any>) => ({
				...prev,
				[header.id]: option,
			}))

			// reset filter value and/or perform new filter render
			if (emptyModes.includes(option)) {
				// will now be empty/notEmpty filter mode
				if (
					currentFilterValue !== ' ' &&
					!emptyModes.includes(prevFilterMode)
				) {
					column.setFilterValue(' ')
				} else if (currentFilterValue) {
					column.setFilterValue(currentFilterValue) // perform new filter render
				}
			} else if (
				columnDef?.filterVariant === 'multi-select' ||
				arrModes.includes(option as string)
			) {
				// will now be array filter mode
				if (
					currentFilterValue instanceof String ||
					(currentFilterValue as any[])?.length
				) {
					column.setFilterValue([])
					setFilterValue?.([])
				} else if (currentFilterValue) {
					column.setFilterValue(currentFilterValue) // perform new filter render
				}
			} else if (
				columnDef?.filterVariant?.includes('range') ||
				rangeModes.includes(option as SRT_FilterOption)
			) {
				// will now be range filter mode
				if (
					!Array.isArray(currentFilterValue) ||
					(!(currentFilterValue as any[])?.every((v) => v === '') &&
						!rangeModes.includes(prevFilterMode))
				) {
					column.setFilterValue(['', ''])
					setFilterValue?.('')
				} else {
					column.setFilterValue(currentFilterValue) // perform new filter render
				}
			} else {
				// will now be single value filter mode
				if (Array.isArray(currentFilterValue)) {
					column.setFilterValue('')
					setFilterValue?.('')
				} else if (
					currentFilterValue === ' ' &&
					emptyModes.includes(prevFilterMode)
				) {
					column.setFilterValue(undefined)
				} else {
					column.setFilterValue(currentFilterValue) // perform new filter render
				}
			}
		}
		setAnchorEl(null)
		onSelect?.()
	}

	const filterOption =
		!!header && columnDef ? columnDef._filterFn : globalFilterFn

	// Using DropdownMenu to replace MUI Menu
	return (
		<DropdownMenu open={!!anchorEl} onOpenChange={() => setAnchorEl(null)}>
			<DropdownMenuContent>
				{(header && column && columnDef
					? (columnDef.renderColumnFilterModeMenuItems?.({
							column: column as any,
							internalFilterOptions,
							onSelectFilterMode: handleSelectFilterMode,
							table,
						}) ??
						renderColumnFilterModeMenuItems?.({
							column: column as any,
							internalFilterOptions,
							onSelectFilterMode: handleSelectFilterMode,
							table,
						}))
					: renderGlobalFilterModeMenuItems?.({
							internalFilterOptions,
							onSelectFilterMode: handleSelectFilterMode,
							table,
						})) ??
					internalFilterOptions.map(
						({ divider, label, option, symbol }, index) => (
							<Fragment key={option}>
								<SRT_ActionMenuItem
									icon={symbol}
									key={option}
									label={label}
									onClick={() =>
										handleSelectFilterMode(option as SRT_FilterOption)
									}
									// selected={option === filterOption}
									table={table}
									// value={option}
								/>
								{divider && <DropdownMenuSeparator />}
							</Fragment>
						)
					)}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
